import React, { useEffect, useState } from 'react';
import GridRow from '../../../../../web-ui-library/src/layouts/Grid/GridRow/GridRow';
import GridColumn from '../../../../../web-ui-library/src/layouts/Grid/GridColumn/GridColumn';
import Pagination from '@components/Pagination';
import { DropDownPosition } from '@components/Pagination/constant';
import DataTableHeader from '../DataTableHeader';
import axiosInstance, { ApiKeys, getApiUrl } from '../../../../utils/ApiHelper';
import { useSelector } from 'react-redux';
import { AgencySummary } from '../../types';
import { RootState } from '@cp/src/store';
import Loader from '../../../../components/Loader/Loader';
import toProperCase from '@cp/src/utils/toProperCase';
import { ICellRendererParams } from 'ag-grid-community';
import cx from 'classnames';
const AgTable = React.lazy(
  () => import(/* webpackChunkName: ['table'] */ '@navi/web-ui/lib/components/AgTable/AgTable')
);

const properCaseRenderer = (value: string) => {
  return toProperCase(value);
};
const columnDefs = [
  {
    field: 'state',
    headerName: 'State',
    cellRenderer: (params: ICellRendererParams) => properCaseRenderer(params?.data?.state)
  },
  {
    field: 'district',
    headerName: 'District',
    cellRenderer: (params: ICellRendererParams) => properCaseRenderer(params?.data?.district)
  },
  {
    field: 'serviceablePincodesInDistrict',
    headerName: 'Serviceable Pincodes',
    cellRenderer: (params: ICellRendererParams) =>
      `${params?.data?.serviceablePincodesInDistrict}/${params?.data?.totalPincodesInDistrict}`
  },
  { field: 'serviceablePincodesInDpd1To30', headerName: '1-30 bucket' },
  { field: 'serviceablePincodesInDpd31To180', headerName: '31-180 bucket' },
  { field: 'serviceablePincodesInDpd180Plus', headerName: '180+ bucket' }
];

type DataTableProps = {
  openUploadDialog: () => void;
};

const initialTableState = {
  serviceability: [],
  pages: { totalElements: 0, totalPages: 0, pageNo: 0, pageSize: 0 }
};

function DataTable({ openUploadDialog }: DataTableProps) {
  const [tableData, setTableData] = useState<AgencySummary>(initialTableState);
  const selectedAgency = useSelector(
    (state: RootState) => state.agencyPincodeMapping.selectedAgency
  );
  const selectedState = useSelector((state: RootState) => state.agencyPincodeMapping.selectedState);
  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedAgency) {
      setTableData(initialTableState);
    }
    if (selectedAgency) {
      setLoading(true);
      const getagencyServiceability = getApiUrl(ApiKeys.GET_AGENCY_SERVICEABILITY);
      const summary = axiosInstance
        .get(getagencyServiceability, {
          params: {
            agencyCode: selectedAgency,
            state: selectedState,
            pageSize: pageSize,
            pageNo: pageNumber - 1
          }
        })
        .then(res => res?.data)
        .then(setTableData)
        .finally(() => {
          setLoading(false);
        });
    }
  }, [selectedAgency, selectedState, pageNumber, pageSize]);

  useEffect(() => {
    setPageSize(10);
    setPageNumber(1);
  }, [selectedAgency, selectedState]);

  const handleOnPageChange = (pageNumber: number) => {
    setPageNumber(pageNumber);
  };

  const handleOnPageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const overlayTemplate = 'Please select an agency to view data';

  return (
    <div className="px-8 py-0">
      <GridRow className="no-gutters">
        <GridColumn md={12} className="pt-0">
          <DataTableHeader openUploadDialog={openUploadDialog} />
        </GridColumn>
      </GridRow>

      <GridRow className="no-gutters">
        <GridColumn md={12} className={'h-[calc(100vh-210px)]'}>
          <AgTable
            defaultColDef={{
              suppressMovable: true
            }}
            gridOptions={{ suppressCellSelection: true }}
            rowData={tableData?.serviceability || []}
            columnDefs={columnDefs}
            theme={'alpine'}
            paginationWrapperClasses={'paginationWrapperClasses'}
            overlayNoRowsTemplate={overlayTemplate}
            noRowsOverlayComponent={() => (
              <div
                className={cx(
                  'flex justify-center items-center h-full text-[--grayscale-2] !text-sm'
                )}
              >
                {' '}
                {selectedAgency ? 'No data available' : 'Please select an agency to view data'}
              </div>
            )}
            PaginationComponent={
              <Pagination
                totalCount={tableData?.pages?.totalElements || 0}
                onPageChange={handleOnPageChange}
                currentPage={pageNumber}
                onPageSizeChange={handleOnPageSizeChange}
                pageSize={pageSize}
                pageNumberDropDownPosition={DropDownPosition.TOP}
              />
            }
            sizeColumnsToFit={true}
          />
        </GridColumn>
      </GridRow>
      <Loader show={loading} className={'overlay'} animate={false} />
    </div>
  );
}

export default DataTable;
